import { pdfjs, Document, Page } from 'react-pdf';
import { useState, useCallback, useRef, useEffect } from 'react';
import { Spin, Button, InputNumber, Tooltip, Space } from 'antd';
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  LeftOutlined,
  RightOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
// import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
// import 'react-pdf/dist/esm/Page/TextLayer.css';
import styles from './index.module.scss';

// 设置 pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

const PdfViewer = ({ 
  url, 
  width,
  showDownload = true,
  showFullscreen = true,
  defaultScale = 1.0,
  minScale = 0.5,
  maxScale = 3.0,
  scaleStep = 0.25,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(null);
  const [scale, setScale] = useState(defaultScale);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // refs
  const containerRef = useRef(null);
  const pageRefs = useRef([]);

  // 文档加载成功回调
  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
    // 初始化页面 refs 数组
    pageRefs.current = Array(numPages).fill(null);
  }, []);

  // 文档加载失败回调
  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF 加载失败:', error);
    setError('PDF 文件加载失败，请检查文件是否有效');
    setLoading(false);
  }, []);

  // 监听滚动事件，更新当前页码
  const handleScroll = useCallback(() => {
    if (!containerRef.current || !numPages) return;
    
    const container = containerRef.current;
    const containerTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    const scrollCenter = containerTop + containerHeight / 3; // 使用 1/3 位置作为判断点
    
    // 找到当前可见的页面
    for (let i = 0; i < numPages; i++) {
      const pageElement = pageRefs.current[i];
      if (pageElement) {
        const pageTop = pageElement.offsetTop;
        const pageBottom = pageTop + pageElement.offsetHeight;
        
        if (scrollCenter >= pageTop && scrollCenter < pageBottom) {
          setCurrentPage(i + 1);
          break;
        }
      }
    }
  }, [numPages]);

  // 添加滚动监听
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  // 滚动到指定页
  const scrollToPage = useCallback((page) => {
    const pageElement = pageRefs.current[page - 1];
    if (pageElement && containerRef.current) {
      containerRef.current.scrollTo({
        top: pageElement.offsetTop - 24, // 减去 padding
        behavior: 'smooth'
      });
    }
  }, []);

  // 跳转到指定页（输入框）
  const goToPage = useCallback((page) => {
    if (page && page >= 1 && page <= numPages) {
      scrollToPage(page);
      setCurrentPage(page);
    }
  }, [numPages, scrollToPage]);

  // 上一页
  const goToPrevPage = useCallback(() => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      scrollToPage(prevPage);
      setCurrentPage(prevPage);
    }
  }, [currentPage, scrollToPage]);

  // 下一页
  const goToNextPage = useCallback(() => {
    if (currentPage < numPages) {
      const nextPage = currentPage + 1;
      scrollToPage(nextPage);
      setCurrentPage(nextPage);
    }
  }, [currentPage, numPages, scrollToPage]);

  // 放大
  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + scaleStep, maxScale));
  }, [scaleStep, maxScale]);

  // 缩小
  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - scaleStep, minScale));
  }, [scaleStep, minScale]);

  // 重置缩放
  const resetZoom = useCallback(() => {
    setScale(defaultScale);
  }, [defaultScale]);

  // 切换全屏
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // 下载 PDF
  const downloadPdf = useCallback(() => {
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = url.split('/').pop() || 'document.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [url]);

  // 渲染加载状态
  const renderLoading = () => (
    <div className={styles.loading}>
      <Spin size="large" tip="正在加载 PDF..." />
    </div>
  );

  // 渲染错误状态
  const renderError = () => (
    <div className={styles.error}>
      <div className={styles.errorIcon}>📄</div>
      <div className={styles.errorText}>{error}</div>
    </div>
  );

  // 渲染所有页面
  const renderAllPages = () => {
    if (!numPages) return null;
    
    return Array.from({ length: numPages }, (_, index) => (
      <div 
        key={`page_${index + 1}`} 
        ref={(el) => (pageRefs.current[index] = el)}
        className={styles.pageWrapper}
      >
        <Page
          pageNumber={index + 1}
          scale={scale}
          width={width}
          className={styles.page}
          renderTextLayer={false}
          renderAnnotationLayer={false}
        />
        <div className={styles.pageNumber}>第 {index + 1} 页</div>
      </div>
    ));
  };

  return (
    <div className={`${styles.pdfViewer} ${isFullscreen ? styles.fullscreen : ''}`}>
      {/* 工具栏 */}
      <div className={styles.toolbar}>
        <Space size="middle">
          {/* 翻页控制 */}
          <Space size="small">
            <Tooltip title="上一页">
              <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={goToPrevPage}
                disabled={currentPage <= 1 || loading}
              />
            </Tooltip>
            <span className={styles.pageInfo}>
              <InputNumber
                min={1}
                max={numPages || 1}
                value={currentPage}
                onChange={goToPage}
                size="small"
                className={styles.pageInput}
                disabled={loading}
              />
              <span className={styles.pageTotal}>/ {numPages || '-'}</span>
            </span>
            <Tooltip title="下一页">
              <Button
                type="text"
                icon={<RightOutlined />}
                onClick={goToNextPage}
                disabled={currentPage >= numPages || loading}
              />
            </Tooltip>
          </Space>

          <div className={styles.divider} />

          {/* 缩放控制 */}
          <Space size="small">
            <Tooltip title="缩小">
              <Button
                type="text"
                icon={<ZoomOutOutlined />}
                onClick={zoomOut}
                disabled={scale <= minScale || loading}
              />
            </Tooltip>
            <Tooltip title="点击重置">
              <Button
                type="text"
                onClick={resetZoom}
                disabled={loading}
                className={styles.scaleBtn}
              >
                {Math.round(scale * 100)}%
              </Button>
            </Tooltip>
            <Tooltip title="放大">
              <Button
                type="text"
                icon={<ZoomInOutlined />}
                onClick={zoomIn}
                disabled={scale >= maxScale || loading}
              />
            </Tooltip>
          </Space>

          <div className={styles.divider} />

          {/* 其他功能 */}
          <Space size="small">
            {showDownload && (
              <Tooltip title="下载">
                <Button
                  type="text"
                  icon={<DownloadOutlined />}
                  onClick={downloadPdf}
                  disabled={loading || !url}
                />
              </Tooltip>
            )}
            {showFullscreen && (
              <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                <Button
                  type="text"
                  icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                  onClick={toggleFullscreen}
                />
              </Tooltip>
            )}
          </Space>
        </Space>
      </div>

      {/* PDF 内容区域 - 可滚动 */}
      <div className={styles.content} ref={containerRef}>
        {error ? (
          renderError()
        ) : (
          <Document
            file={url}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={renderLoading()}
            className={styles.document}
          >
            {renderAllPages()}
          </Document>
        )}
      </div>
    </div>
  );
};

export default PdfViewer;
